import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle } from 'lucide-react';

export default function HelpDialog({ title = "Help", content, size = "default", iconSize: customIconSize, ariaLabel, iconClassName }) {
  const [open, setOpen] = useState(false);
  
  // Check if tooltips are hidden
  const showTooltips = localStorage.getItem('showTooltips') !== 'false';

  // Convert markdown-style headers to HTML
  const formatContent = (text) => {
    if (!text) return '';
    
    // Split by lines and process
    const lines = text.split('\n');
    let html = '';
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLine = i < lines.length - 1 ? lines[i + 1] : '';
      
      // Handle ### headers (smaller)
      if (line.startsWith('### ')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h3 class="text-lg font-bold mt-3 mb-2">${line.substring(4)}</h3>`;
      }
      // Handle ## headers
      else if (line.startsWith('## ')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<h2 class="text-xl font-bold mt-4 mb-2">${line.substring(3)}</h2>`;
      }
      // Handle list items (show bullets)
      else if (line.trim().startsWith('- ')) {
        if (!inList) {
          html += '<ul class="list-disc ml-6 mb-2">';
          inList = true;
        }
        const content = line.trim().substring(2);
        const formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                 .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                 .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>');
        html += `<li class="mb-1">${formatted}</li>`;
        
        // Close list if next line is not a list item
        if (!nextLine.trim().startsWith('- ')) {
          html += '</ul>';
          inList = false;
        }
      }
      // Handle **bold** or *italic* or <u>underline</u> text
      else if (line.includes('**') || line.includes('*') || line.includes('<u>')) {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                             .replace(/\*(.*?)\*/g, '<em>$1</em>')
                             .replace(/<u>(.*?)<\/u>/g, '<u>$1</u>');
        html += `<p class="mb-2">${formatted}</p>`;
      }
      // Empty lines - reduced spacing
      else if (line.trim() === '') {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        // Skip empty lines to reduce spacing
      }
      // Regular paragraphs
      else {
        if (inList) {
          html += '</ul>';
          inList = false;
        }
        html += `<p class="mb-2">${line}</p>`;
      }
    }
    
    // Close list if still open at end
    if (inList) {
      html += '</ul>';
    }
    
    return html;
  };

  const iconSize = customIconSize || (size === "large" ? 20 : 16);

  // Don't render if tooltips are hidden
  if (!showTooltips) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        title={title}
        aria-label={ariaLabel || title}
        className={iconClassName}
      >
        <HelpCircle size={iconSize} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div 
            className="text-sm leading-relaxed overflow-y-auto pr-4 -mr-2"
            dangerouslySetInnerHTML={{ __html: formatContent(content) }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
